import { Tooltip } from './Tooltip';

// Colour is hashed from the value, not mapped: new status strings style themselves.
const SOLID_BG_PALETTE = [
    'bg-blue-600',
    'bg-emerald-600',
    'bg-violet-600',
    'bg-amber-600',
    'bg-teal-600',
    'bg-rose-600',
    'bg-indigo-600',
    'bg-orange-600',
    'bg-cyan-600',
    'bg-pink-600'
];

function hashStatusValue(value: string): number {
    let h = 0;
    for (let i = 0; i < value.length; i++) {
        h = ((h << 5) - h + value.charCodeAt(i)) | 0;
    }
    return Math.abs(h);
}

/** Exported so category dots outside the pill share its hashed palette. */
export function getSolidStatusBgColor(value: string): string {
    return SOLID_BG_PALETTE[hashStatusValue(value) % SOLID_BG_PALETTE.length];
}

interface SolidStatusPillProps {
    /** Raw status value; hashed to pick a consistent colour across the app. */
    value: string;
    /** Display text. Defaults to `value` for callers with no separate humanized label. */
    label?: string;
    className?: string;
    /** Small-badge metrics for pills inline with 10px tags; a prop because
     * className can't override same-specificity utilities. */
    compact?: boolean;
    /** Explicit tone (bg + text) that *replaces* the hashed bg and text-white —
     * same-specificity utilities resolve by stylesheet order, not class order. */
    toneClassName?: string;
    /** Replaces the dot's translucent white, e.g. `bg-current` on a light tone. */
    dotClassName?: string;
    /** Hover text when it should say more than the label; defaults to the label. */
    tooltip?: string;
}

/** Solid colour-hashed status pill; truncates, full text on hover via tooltip. */
export function SolidStatusPill({
    value,
    label,
    className = '',
    toneClassName,
    dotClassName = 'bg-white/40',
    compact = false,
    tooltip
}: SolidStatusPillProps) {
    const text = label ?? value;
    const tone = toneClassName ?? `text-white ${getSolidStatusBgColor(value)}`;
    const sizeClasses = compact
        ? 'gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide'
        : 'gap-1.5 rounded-md px-2.5 py-1 text-[11px]';
    return (
        <Tooltip content={tooltip ?? text} side="top">
            <span
                className={`inline-flex items-center max-w-full font-semibold ${sizeClasses} ${tone} ${className}`}
            >
                <span
                    className={`${compact ? 'w-1 h-1' : 'w-1.5 h-1.5'} rounded-full flex-shrink-0 ${dotClassName}`}
                />
                <span className="truncate">{text}</span>
            </span>
        </Tooltip>
    );
}
